import { Wand2, CheckCircle2 } from "lucide-react";

import { StudioPageLayout } from "@/components/flowly/StudioPageLayout/StudioPageLayout";
import { MediaGridItem } from "@/components/flowly/MediaGridItem/MediaGridItem";
import { CreationsGallery } from "@/components/flowly/CreationsGallery/CreationsGallery";
import { GalleryItemBadges } from "@/components/flowly/GalleryItemBadges/GalleryItemBadges";
import { GenerationBar } from "@/components/flowly/GenerationBar/GenerationBar";
import { ImageLightbox } from "@/components/flowly/Lightbox/ImageLightbox/ImageLightbox";
import { HowItWorks } from "@/components/flowly/HowItWorks/HowItWorks";
import { MaskCanvas } from "@/components/flowly/MaskCanvas/MaskCanvas";
import { OnboardingTour } from "@/components/flowly/OnboardingTour/OnboardingTour";
import { ImageGeneratorSourceUpload } from "@/components/flowly/ImageGeneratorSourceUpload/ImageGeneratorSourceUpload";
import { ImageGeneratorPromptArea } from "@/components/flowly/ImageGeneratorPromptArea/ImageGeneratorPromptArea";
import { downloadMedia } from "@/lib/mediaUtils";
import { useImageGenerator } from "./useImageGenerator";

export default function ImageGeneratorPage() {
  const {
    // Refs
    fileInputRef,
    galleryRef,

    // Upload
    imageUrl,
    isDragging,
    handleFileSelect,
    handleDrop,
    handleClearImage,
    onDragOver,
    onDragLeave,

    // Prompt
    prompt,
    setPrompt,
    isEnhancingPrompt,
    handleEnhancePrompt,

    // Generation options
    quantity,
    setQuantity,
    aspectRatio,
    setAspectRatio,
    availableAspectRatios,
    resolution,
    availableResolutions,
    handleResolutionChange,
    imageModel,
    handleImageModelChange,
    getEditCreditCost,
    getImageModelCost,
    costsLoading,

    // Mask
    maskImageUrl,
    showMaskCanvas,
    setShowMaskCanvas,
    handleMaskConfirm,

    // Reference images
    referenceImages,
    maxReferenceImages,
    handleReferenceImagesAdd,
    handleReferenceImageRemove,

    // Generation
    isGenerating,
    handleGenerate,
    pendingEdits,
    pendingImages,
    imageCompletingIds,
    failedImages,
    handleClearFailed,

    // Models
    models,
    modelWarning,

    // Gallery
    galleryImages,
    handleGalleryItemClick,

    // Lightbox
    selectedImage,
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,
    handleCloseLightbox,
    handleRegenerate,
    handleAnimate,
    handleLightboxDownload,
    handleShare,
    handleDelete,

    // Disabled click
    handleDisabledClick,

    // i18n
    t,
  } = useImageGenerator();

  return (
    <StudioPageLayout
      activeTab="images"
      activeApp="imageGenerator"
      seoHeadingKey="imageGenerator"
      headerChildren={<HowItWorks flowId="imageGenerator" />}
      footer={
        !selectedImage ? (
          <GenerationBar
            onGenerate={handleGenerate}
            generating={false}
            disabled={!prompt.trim()}
            creditCost={quantity * getEditCreditCost(resolution, imageModel)}
            costsLoading={costsLoading}
            buttonLabel={t.imageGenerator.editImageBtn}
            selectedModelId={imageModel}
            onModelChange={handleImageModelChange}
            mediaType="image"
            aspectRatio={aspectRatio}
            onAspectRatioChange={setAspectRatio}
            availableAspectRatios={availableAspectRatios}
            resolution={resolution}
            onResolutionChange={handleResolutionChange}
            availableImageResolutions={availableResolutions}
            quantity={quantity}
            onQuantityChange={setQuantity}
            modelWarning={modelWarning}
            getModelCost={(id) => getImageModelCost(id, models, resolution, "image_edit")}
            onDisabledClick={handleDisabledClick}
          />
        ) : undefined
      }
      lightbox={
        selectedImage ? (
          <ImageLightbox
            imageUrl={selectedImage.url}
            imageId={selectedImage.id}
            referenceImageUrl={selectedImage.referenceImageUrls[0]}
            resolution={selectedImage.resolution}
            aspectRatio={selectedImage.aspectRatio}
            prompt={selectedImage.prompt}
            model={selectedImage.model}
            pipeline={selectedImage.pipeline}
            createdAt={selectedImage.createdAt}
            onClose={handleCloseLightbox}
            onRegenerate={handleRegenerate}
            onAnimate={handleAnimate}
            onDownload={handleLightboxDownload}
            onShare={handleShare}
            onDelete={handleDelete}
            navigation={
              galleryImages.length > 1
                ? {
                    currentIndex: currentMediaIndex,
                    totalItems: galleryImages.length,
                    canGoNext: canNavigate,
                    canGoPrev: canNavigate,
                    onNext: goToNextItem,
                    onPrev: goToPrevItem,
                  }
                : undefined
            }
          />
        ) : undefined
      }
      overlays={
        <>
          {showMaskCanvas && imageUrl && (
            <MaskCanvas
              imageUrl={imageUrl}
              onConfirm={handleMaskConfirm}
              onCancel={() => setShowMaskCanvas(false)}
            />
          )}
          <OnboardingTour pageReady={!isGenerating} />
        </>
      }
    >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
          {/* Results or Prompt Area */}
          <div data-scroll-target="prompt">
            <ImageGeneratorPromptArea
              prompt={prompt}
              onPromptChange={setPrompt}
              onEnhancePrompt={handleEnhancePrompt}
              isEnhancingPrompt={isEnhancingPrompt}
            />
          </div>

          {/* Source Image or Upload */}
          <ImageGeneratorSourceUpload
            imageUrl={imageUrl}
            maskImageUrl={maskImageUrl}
            isDragging={isDragging}
            fileInputRef={fileInputRef}
            onFileSelect={handleFileSelect}
            onDrop={handleDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClearImage={handleClearImage}
            onOpenMask={() => setShowMaskCanvas(true)}
            referenceImages={referenceImages}
            onReferenceImagesAdd={handleReferenceImagesAdd}
            onReferenceImageRemove={handleReferenceImageRemove}
            maxReferenceImages={maxReferenceImages}
          />
        </div>

      {/* User Creations Gallery */}
      <CreationsGallery
        title={t.imageGenerator.yourCreations}
        galleryRef={galleryRef}
        pendingCount={pendingEdits}
        pendingLabel={t.imageGenerator.editingImage}
        pendingItems={pendingImages.map((img) => ({ id: img.id, createdAt: img.created_at }))}
        pendingMediaType="image"
        completingIds={imageCompletingIds}
        failedItems={failedImages}
        failedLabel={t.generate.failedRefunded}
        onClearFailed={handleClearFailed}
        className="mt-10 mb-10"
        emptyIcon={Wand2}
        emptyTitle={t.imageGenerator.noEditsYet}
        emptySubtitle={t.imageGenerator.startEditing}
      >
          {galleryImages.map((item, idx) => (
              <MediaGridItem
                key={item.id}
                imageUrl={item.displayUrl}
                hoverText={t.imageGenerator.clickToOpen}
                alt={`Gallery ${idx + 1}`}
                onClick={() => handleGalleryItemClick(item)}
                onDownload={() => downloadMedia(item.url, "todoai-image", "png")}
                downloadLabel={t.lightbox.download}
              >
                <GalleryItemBadges item={item} />
                {imageUrl === item.url && (
                  <div className="absolute bottom-2 end-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
              </MediaGridItem>
          ))}
      </CreationsGallery>
    </StudioPageLayout>
  );
}
